import { Badge, Box, Button, Divider, Flex, Group, Text, ThemeIcon } from "@mantine/core"
import { ArrowLeftIcon } from "@phosphor-icons/react";
import { ReactNode } from "react"
import { useNavigate } from "react-router"
import { Breadcrumb, BreadcrumbItem } from "./breadcrumb"
import { StatusBadge } from "./status-badge"

type Props = {
  title: ReactNode
  subtitle?: ReactNode
  /** Optional large icon displayed to the left of the title */
  icon?: ReactNode
  /** Optional status pill rendered next to the title */
  status?: { color?: string; sentName?: string; name?: string } | null
  /** Right-aligned action buttons / chips */
  actions?: ReactNode
  /** Breadcrumb trail rendered above the header row */
  breadcrumbs?: BreadcrumbItem[]
  /** Show a back button on the left; either give it an onClick or let it navigate(-1) */
  onBack?: () => void
  backLabel?: string
  /** Override navigation target when onBack isn't provided. Uses navigate(path) */
  backPath?: string
}

/**
 * Page-level header bar used on detail/edit pages.
 * Renders optional breadcrumb → flex row with (back button | icon | title+subtitle+status) on the left
 * and an actions slot on the right.
 */
export function PageHeader({
  title,
  subtitle,
  icon,
  status,
  actions,
  breadcrumbs,
  onBack,
  backLabel,
  backPath,
}: Props) {
  const navigate = useNavigate()

  const handleBack = () => {
    if (onBack) return onBack()
    if (backPath) return navigate(backPath)
  }

  return (
    <Box>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Box mb="md">
          <Breadcrumb items={breadcrumbs} />
        </Box>
      )}

      <Flex justify="space-between" align="center" mb="xl" wrap="wrap" gap="md">
        <Group gap="md" wrap="nowrap">
          {(onBack || backPath) && (
            <Button
              variant="subtle"
              color="gray"
              leftSection={<ArrowLeftIcon size={18} />}
              onClick={handleBack}
            >
              {backLabel ?? ""}
            </Button>
          )}
          {icon && <Divider orientation="vertical" />}
          {icon && (
            <ThemeIcon size={48} radius="xl" variant="light" color="brand">
              {icon}
            </ThemeIcon>
          )}
          <Box>
            <Group gap="sm">
              <Text size="xl" fw={700}>{title}</Text>
              {status && <StatusBadge status={status} size="lg" />}
              {status === undefined && null /* explicit no-status */}
            </Group>
            {subtitle && (
              <Text size="sm" c="dimmed">{subtitle}</Text>
            )}
          </Box>
        </Group>

        {actions && <Group gap="sm">{actions}</Group>}
      </Flex>
    </Box>
  )
}

// Re-export so consumers get the BreadcrumbItem type from the same module
export type { BreadcrumbItem }
// Optional re-export for consumers that want to render Badges next to the title slot
export { Badge }
